/**
 * Grid column definitions for the Structures data grid
 *
 * This file re-exports the unified field definitions for backward compatibility.
 * The actual definitions are in fieldDefinitions.js
 */

import {
  getStructureGridColumns as getColumns,
  DEFAULT_COLUMN_VISIBILITY as DEFAULT_VISIBILITY
} from './fieldDefinitions.jsx';

// Re-export for backward compatibility
export const getStructureGridColumns = getColumns;
export const DEFAULT_COLUMN_VISIBILITY = DEFAULT_VISIBILITY;

// Legacy implementation kept for reference (to be removed)
const getStructureGridColumnsOLD = ({ isPreviewMode = false, previewData = [] } = {}) => {
  return [
    {
      field: 'id_',
      headerName: 'ID',
      width: 48,
      type: 'number',
      valueGetter: (value, row) => row?.id_ || row?.id
    },
    {
      field: 'source_path',
      headerName: 'Source Path',
      width: 160,
      flex: 1,
      valueGetter: (value, row) => row?.source_path || row?.filename || `prep_${row?.id_ || row?.id}`
    },
    {
      field: 'project',
      headerName: 'Project',
      width: 96,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.project !== undefined) return preview.project;
        }
        return row?.project || '';
      }
    },
    {
      field: 'target',
      headerName: 'Target',
      width: 80,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.target !== undefined) return preview.target;
        }
        return row?.target || '';
      }
    },
    {
      field: 'mutation',
      headerName: 'Mutation',
      width: 80,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.mutation !== undefined) return preview.mutation;
        }
        return row?.mutation || '';
      }
    },
    {
      field: 'subgroup',
      headerName: 'Subgroup',
      width: 80,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.subgroup !== undefined) return preview.subgroup;
        }
        return row?.subgroup || '';
      }
    },
    {
      field: 'prep_workflow',
      headerName: 'Prep Workflow',
      width: 96,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.prep_workflow !== undefined) return preview.prep_workflow;
        }
        return row?.prep_workflow || '';
      }
    },
    {
      field: 'batch',
      headerName: 'Batch',
      width: 96,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.batch !== undefined) return preview.batch;
        }
        return row?.batch || '';
      }
    },
    {
      field: 'ligand_name',
      headerName: 'Ligand Name',
      width: 96,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.ligand_name !== undefined) return preview.ligand_name;
        }
        return row?.ligand_name || '';
      }
    },
    {
      field: 'ligand_tag',
      headerName: 'Ligand Tag',
      width: 80,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.ligand_tag !== undefined) return preview.ligand_tag;
        }
        return row?.ligand_tag || '';
      }
    },
    {
      field: 'author',
      headerName: 'Author',
      width: 80,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.author !== undefined) return preview.author;
        }
        return row?.author || '';
      }
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        if (isPreviewMode) {
          const preview = previewData.find(p => p.id === row.id);
          if (preview?.notes !== undefined) return preview.notes;
        }
        return row?.notes || '';
      }
    },
    {
      field: 'prepared_at',
      headerName: 'Prepared',
      width: 80,
      type: 'date',
      valueGetter: (value, row) => {
        const dateStr = row?.prepared_at;
        if (!dateStr) return null;
        return new Date(dateStr);
      },
      renderCell: (params) => {
        const date = params.value;
        if (!date) return '-';
        const yy = date.getFullYear().toString().slice(-2);
        const mm = (date.getMonth() + 1).toString().padStart(2, '0');
        const dd = date.getDate().toString().padStart(2, '0');
        return `${yy}/${mm}/${dd}`;
      },
      // Custom filter operators for flexible date matching
      filterOperators: [
        {
          value: 'is',
          label: 'is',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;

            // Parse the filter value flexibly
            const filterValue = String(filterItem.value).trim();

            return (value) => {
              if (!value) return false;

              const date = value instanceof Date ? value : new Date(value);
              if (isNaN(date.getTime())) return false;

              const yy = date.getFullYear().toString().slice(-2);
              const yyyy = date.getFullYear().toString();
              const mm = (date.getMonth() + 1).toString().padStart(2, '0');
              const dd = date.getDate().toString().padStart(2, '0');

              // Check various date formats
              const dateStrings = [
                `${yy}/${mm}/${dd}`,
                `${yy}${mm}${dd}`,
                `${yyyy}-${mm}-${dd}`,
                `${mm}/${dd}/${yy}`,
                `${dd}/${mm}/${yy}`,
                `${yyyy}${mm}${dd}`
              ];

              // Check if filter matches any format (partial or full)
              return dateStrings.some(dateStr =>
                dateStr.includes(filterValue) ||
                filterValue.includes(dateStr)
              );
            };
          }
        },
        {
          value: 'after',
          label: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            const filterDate = new Date(filterItem.value);
            filterDate.setHours(0, 0, 0, 0); // Compare dates only, not times

            return (value) => {
              if (!value) return false;
              const date = value instanceof Date ? value : new Date(value);
              const compareDate = new Date(date);
              compareDate.setHours(0, 0, 0, 0);
              return compareDate > filterDate;
            };
          }
        },
        {
          value: 'before',
          label: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            const filterDate = new Date(filterItem.value);
            filterDate.setHours(23, 59, 59, 999); // Include entire day

            return (value) => {
              if (!value) return false;
              const date = value instanceof Date ? value : new Date(value);
              const compareDate = new Date(date);
              compareDate.setHours(0, 0, 0, 0);
              return compareDate < filterDate;
            };
          }
        }
      ]
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        const tags = row?.tags || [];
        return Array.isArray(tags) ? tags.join(' ') : String(tags);
      }
    }
  ];
};

/**
 * Helper function to get preview value for a field
 * Extracted for potential reuse
 */
export const getPreviewValue = (row, field, previewData) => {
  const preview = previewData.find(p => p.id === row.id);
  return preview?.[field];
};
